import { z } from 'zod';
import { uuidV7Schema } from '../uuid.js';
import { normalizeLogin } from './login.js';
import { coveredInseeCodesSchema } from './team-coverage.js';
/**
 * `Team` — pas de `sector_id` : le secteur d'une équipe se déduit de sa couverture (M22),
 * hors périmètre de ce schéma. `name` reprend les initiales des deux agents séparées par
 * `·` (`JL·TB`), champ libre et non contraint : renommer l'équipe ne change pas son `login`
 * (`eq.jltb`, cf. `login.ts`).
 */
export const teamSchema = z.object({
    id: uuidV7Schema,
    name: z.string().min(1),
    available: z.boolean(),
});
/**
 * Ce que l'appelant fournit pour créer une équipe (M7, M3) : `id` (uuid v7) et `available`
 * (`true` par défaut) se dérivent côté service, comme `createUserSchema` le fait pour `User`.
 *
 * ⚠️ **`strictObject`, pas `object`** : un `id` ou un `available` de trop se rejette au lieu
 * de se retirer en silence — l'appelant croirait l'avoir imposé.
 *
 * ⚠️ **`name` n'est pas fourni non plus, il est dérivé** — corrigé le 04/09/2026. Le prototype
 * validé fait de la création une page où l'on coche deux agents, et le nom s'écrit tout seul
 * (`t.name = t.a + '·' + t.b`, cf. `buildTeamName`) : le laisser entrer ici permettrait de nommer
 * une équipe autrement que ses membres dès sa naissance, ce que l'écran ne propose pas. M22 garde
 * le champ **libre en base** — une équipe se renomme ensuite, et ce sera un autre geste.
 *
 * ⚠️ **`agents` est un tuple de deux, pas un tableau** : le plafond de M12 entre ainsi dans le
 * **type**, et une création à un ou trois agents ne compile pas côté client — là où un tableau
 * aurait renvoyé tout le contrôle au comptage de service.
 */
export const createTeamSchema = z.strictObject({
    agents: z.tuple([uuidV7Schema, uuidV7Schema]),
    /**
     * Le « Secteur d'intervention » que la page de création fait cocher (M22) — facultatif : une
     * équipe peut naître sans commune et recevoir la sienne ensuite (`PUT /teams/:id/coverage`).
     *
     * ⚠️ **Dans le même corps, et non dans un second appel** : la couverture s'écrit dans la
     * transaction du groupe, pour la même raison que le compte de la tablette — enchaînés depuis le
     * navigateur, le premier échec réseau laissait une équipe sans le secteur qu'on venait de cocher.
     */
    inseeCodes: coveredInseeCodesSchema.optional(),
});
/**
 * Un identifiant de connexion **saisi à la main**, replié sur la forme de M6 : accents repliés,
 * minuscules, `[a-z0-9.]` — `Éq. JL TB` devient `eq.jltb`.
 *
 * ⚠️ **Il replie, il ne refuse pas** (M21) : la saisie arrive d'un humain, pas d'un programme, et
 * un identifiant à majuscules ou à espaces est une faute de frappe, pas une demande à rejeter.
 * Sans ce repli on fabriquerait un identifiant **intapable sur une tablette** — et c'est
 * exactement ce que la connexion d'une équipe ne pardonne pas.
 *
 * ⚠️ **Le refus, lui, porte sur le vide, et sur le vide seul** : une saisie qui ne garde ni
 * lettre, ni chiffre, ni point (« … », « --- ») se replie en rien, et `User.login` est
 * `NOT NULL UNIQUE` (M6). C'est la première des trois exceptions de M21 — l'intégrité
 * structurelle —, et le message dit la forme attendue plutôt que de nommer une expression
 * régulière.
 *
 * ⚠️ **Le point ASCII traverse**, `normalizeLogin` le gardant (`login.ts`) : « ... » est accepté
 * tel quel, et c'est le bon comportement. Refuser une valeur non vide et unique sortirait des
 * trois exceptions de M21, et l'écran replie **à la frappe** — l'humain voit `...` avant de
 * valider. Ce `refine` ne filtre donc pas la ponctuation, il n'écarte que le résultat vide.
 */
export const teamLoginSchema = z
    .string()
    .transform(normalizeLogin)
    .refine((login) => login !== '', {
    error: "cet identifiant ne garde aucune lettre ni aucun chiffre une fois normalisé — il s'écrit " +
        'en minuscules non accentuées, chiffres et points (M6).',
});
/**
 * Ce qu'on corrige sur une équipe **après** sa création (M22, M6) : son nom, et l'identifiant de
 * connexion de sa tablette. Deux champs, deux tables — `Team.name` et `User.login` du compte
 * partagé —, mais **un seul geste** : c'est la même fiche, et le login se propose à partir du nom.
 *
 * ⚠️ **`login` vise le compte partagé de l'équipe, jamais un agent.** Il n'entre pas dans
 * `updateUserSchema` (M6 : le login d'un compte nominatif est stocké une fois pour toutes), et
 * cette route existe précisément pour ne pas ouvrir celui-là — même raison qui a fait naître
 * `GET /teams/candidates` plutôt qu'ouvrir `GET /users`.
 *
 * ⚠️ **Rien n'est dérivé ici.** `buildTeamName` et `buildTeamLogin` **proposent** un défaut à
 * l'écran, qui le pré-remplit ; ce qui arrive dans ce schéma est ce qu'un humain a validé.
 * Recalculer côté serveur écraserait au prochain changement de binôme une valeur choisie à la
 * main, sans que personne l'ait demandé (garde-fou n°3 : on propose, on signale, l'humain
 * tranche).
 *
 * ⚠️ **`strictObject`, pas `object`** : un `available` ou un `id` de trop se rejette au lieu de se
 * retirer en silence — l'appelant croirait l'avoir imposé.
 *
 * ⚠️ **Au moins un champ**, comme `updateUserSchema` : un `PATCH` vide ne demande rien, et
 * Drizzle refuse de toute façon un `set({})`.
 */
export const updateTeamSchema = z
    .strictObject({
    name: z.string().min(1).optional(),
    login: teamLoginSchema.optional(),
})
    .refine((patch) => Object.keys(patch).length > 0, { error: 'aucun champ à modifier' });
//# sourceMappingURL=team.js.map